import React, { useState, useEffect } from 'react';
import { IconLabel } from '@/components/common/elements/IconLabel';
import LoadingSpinner from '@/components/common/elements/LoadingSpinner';
import ResultModal from '@/components/common/elements/ResultModal';
import styled from '@emotion/styled';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Text from '@/components/common/elements/Text';
import { Seminar } from './types';
import { api } from '@/api/repository/axios';
import { loadTossPayments, ANONYMOUS } from '@tosspayments/tosspayments-sdk';
import { setPaymentMode } from '@/native/paymentNavigation';
import { REFUND_POLICY_RULES, REFUND_WITHDRAWAL_RULE } from '@/pages/Legal/refundPolicyContent';
import CustomModal from '@/components/common/elements/Modal';
import { default as ModalContainer } from '@/components/common/elements/Container';
import { default as ModalText } from '@/components/common/elements/Text';
import { default as ModalButton } from '@/components/common/elements/Button';
import { useIsMobile } from '@/common/hooks';
import NaverMap from '@/components/common/organism/NaverMap';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { useMediaQuery } from '@mui/material';
import { toastState } from '@/state/toastState';
import { formatMonthDay, formatOf, isGeneralOnline, isOpenSchedule, statusBadgeOf, vodRefundNotes } from '@/components/domain/seminar/seminarDisplay';
import DetailHero from './components/detail/DetailHero';
import CollapsibleDescription from './components/detail/CollapsibleDescription';
import { shareCurrentPage, type ShareResult } from '@/common/shareLink';
import { DESCRIPTION_FOLD_HEIGHT } from './components/detail/descriptionFold';
import DetailTitleCard from './components/detail/DetailTitleCard';
import DetailInfoCard, { SessionList } from './components/detail/DetailInfoCard';
import DetailSummaryCard from './components/detail/DetailSummaryCard';
import DetailUnavailable from './components/detail/DetailUnavailable';
import ReportBlockMenu from '@/components/moderation/ReportBlockMenu';
import GeneralNoticeCard, { GENERAL_NOTICE_LINES } from './components/detail/GeneralNoticeCard';
import { ApplyActionState, ApplyBottomBar } from './components/detail/ApplyAction';
import { buildDetailInfo } from './components/detail/detailInfo';
import SeminarCouponCard from './components/detail/SeminarCouponCard';
import PurchaseCouponPicker, { SelectedPurchaseCoupon } from './components/detail/PurchaseCouponPicker';
import { useSeminarCoupons } from './components/detail/useSeminarCoupons';
import { cancelCouponLine, COUPON_NO_RESTORE_NOTICE, COUPON_RESTORE_NOTICE, errorMessageOf, won } from '@/components/domain/coupon/couponFormat';
import { stashPaymentCoupon } from './paymentCouponStash';
import type { PaidPrice } from './components/detail/CouponFee';
import { notifyCouponCountChanged } from '@/components/domain/coupon/useMyCouponCount';
import type { RefundQuoteCoupon, SeminarClaimableCoupon, SeminarPaymentPrepareResult } from '@/api/repository/types/types';
import { DCard, DCardTitle, DCardTitleLink, DetailPage, KvGrid, MainColumn, MOBILE_QUERY, SideColumn } from './components/detail/detailStyles';
import { userAtom } from '@/state/loginState';
import { profileCache } from '@/utils/profileCache';
import { PageContainer } from '@/styles/components';

import { designSystem } from '@/styles/theme';
import { claudeTokens } from '@/styles/claudeTokens';
import ButtonSpinner from '@/components/common/elements/ButtonSpinner';
import { ChevronLabel } from '@/components/common/elements/IconLabel';
import { track } from '@/common/analytics';
import { UI_KIND, UI_SCREEN } from '@/common/uiEventNames';

const CustomPageContainer = styled(PageContainer)`
  max-width: none;
  padding-left: 0;
  padding-right: 0;
  background: transparent;

  @media (max-width: 768px) {
    /* 모바일은 다른 상세(채용/속닥속닥)와 상단 여백 통일 */
    padding-top: 0;
  }
`;

/** 위치 카드의 주소 한 줄 */
/** 지도 아래 "주소" 줄 — 정보 표(일정·장소)와 같은 라벨 열 + 값 열 */
const LocationAddressGrid = styled(KvGrid)`
  margin-top: 12px;
`;

const ActionSection = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger'; fullWidth?: boolean }>`
  width: ${props => props.fullWidth ? '100%' : 'auto'};
  padding: 14px 24px;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background-color: ${designSystem.colors.primary};
          color: white;
          &:hover { background-color: ${designSystem.colors.primaryDark}; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3); }
          &:disabled { background-color: #ccc; cursor: not-allowed; transform: none; }
        `;
      case 'danger':
        return `
          background-color: #ef4444;
          color: white;
          &:hover { background-color: #dc2626; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3); }
        `;
      default:
        return `
          background-color: #f1f5f9;
          color: #475569;
          border: 1px solid #e2e8f0;
          &:hover { background-color: #e2e8f0; transform: translateY(-2px); }
        `;
    }
  }}
`;

const ReviewSection = styled.section`
  margin-top: 8px;
  padding: 14px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  @media (min-width: 769px) {
    margin-top: 12px;
    padding: 18px;
  }
`;

const ReviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`;

const RatingBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  background-color: #fff7e6;
  color: #b45309;
  font-weight: 600;
`;

const ReviewForm = styled.div`
  border: 1px solid #e5e8ec;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  background: #fdfdfd;
`;

const ReviewTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 12px;
  font-size: 14px;
  resize: vertical;
  font-family: inherit;
  color: #111827;
  &:focus {
    outline: none;
    border-color: ${designSystem.colors.primary};
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.15);
  }
`;

const ReviewControls = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
`;

const RatingSelect = styled.select`
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  background: white;
  color: #111827;
  min-width: 120px;
`;

const ReviewList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ReviewCard = styled.div`
  border: 1px solid #e5e8ec;
  border-radius: 12px;
  padding: 18px;
  background: #fff;
`;

const ReviewMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
`;

const ReviewContent = styled.p`
  margin: 0;
  color: #0f172a;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
`;

const DeletedReviewText = styled.p`
  margin: 0;
  color: #9ca3af;
  font-style: italic;
  white-space: pre-wrap;
`;

const ReviewActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
  margin-top: 12px;
  flex-wrap: wrap;
  width: 100%;
`;

const TextButton = styled.button`
  background: none;
  border: none;
  color: ${designSystem.colors.primary};
  font-weight: 600;
  padding: 0;
  cursor: pointer;
  font-size: 14px;
  &:disabled {
    color: #cbd5f5;
    cursor: not-allowed;
  }
`;

const ReplyList = styled.div`
  margin-top: 12px;
  border-left: 2px solid #e5e8ec;
  padding-left: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ReplyCard = styled.div`
  background: #f9fafb;
  border-radius: 8px;
  padding: 12px;
  border: 1px solid #e5e8ec;
`;

const ReplyMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 13px;
  color: ${claudeTokens.color.text2};
  flex-wrap: wrap;
`;

const ReplyMetaRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ReplyFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  font-size: 12px;
  color: #94a3b8;
  margin-top: 4px;
`;

const ReplyContent = styled.div`
  font-size: 14px;
  color: #1e293b;
  white-space: pre-wrap;
  line-height: 1.5;
  word-break: break-word;
`;

const ReplyInput = styled.textarea`
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 10px;
  min-height: 80px;
  font-size: 14px;
  resize: vertical;
  font-family: inherit;
  &:focus {
    outline: none;
    border-color: ${designSystem.colors.primary};
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.15);
  }
`;

const ReplyActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  justify-content: flex-end;
  width: 100%;
`;

const MoreButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: ${designSystem.colors.primary};
  }

  svg {
    width: 18px;
    height: 18px;
    stroke: currentColor;
  }
`;

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 36px;
  right: 0;
  min-width: 120px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.18);
  padding: 6px 0;
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  flex-direction: column;
  z-index: 1100;
`;

const DropdownItem = styled.button`
  padding: 10px 16px;
  background: transparent;
  border: none;
  text-align: left;
  font-size: 0.9rem;
  color: #0f172a;
  cursor: pointer;

  &:hover {
    background: #f8fafc;
  }

  &.delete {
    color: #dc2626;
  }
`;

const AlertBox = styled.div<{ variant: 'error' | 'warning' | 'info' }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  margin-top: 8px;
  border-left: 4px solid;

  ${props => {
    switch (props.variant) {
      case 'error':
        return `
          background: #fef2f2;
          border-color: #dc2626;
          color: #7f1d1d;
        `;
      case 'warning':
        return `
          background: #fffbeb;
          border-color: #f59e0b;
          color: #78350f;
        `;
      case 'info':
        return `
          background: #eff6ff;
          border-color: #3b82f6;
          color: #1e40af;
        `;
    }
  }}

  @media (max-width: 768px) {
    padding: 14px 16px;
    font-size: 14px;
  }
`;

const AlertIcon = styled.div`
  flex-shrink: 0;
  font-size: 20px;
  line-height: 1;
  margin-top: 2px;
`;

const AlertContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AlertTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;
`;

const AlertMessage = styled.div`
  font-size: 14px;
  line-height: 1.6;
  opacity: 0.9;
  white-space: pre-wrap;
  word-break: break-word;
`;

const formatReviewDate = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const InstructorProfileSection = styled.section`
  margin-top: 8px;
  padding: 14px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  @media (min-width: 769px) {
    margin-top: 12px;
    padding: 18px;
  }
`;

const InstructorProfileCard = styled.div`
  border: 1px solid #e5e8ec;
  border-radius: 12px;
  padding: 24px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  display: flex;
  align-items: center;
  gap: 20px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
    border-color: ${designSystem.colors.primary};
  }

  @media (max-width: 768px) {
    padding: 20px;
    gap: 16px;
  }
`;

const InstructorThumbnail = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 42px;
  font-weight: bold;
  flex-shrink: 0;
  overflow: hidden;
  border: 2px solid #e5e7eb;

  @media (max-width: 768px) {
    width: 90px;
    height: 90px;
    font-size: 32px;
  }
`;

const InstructorThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const DefaultProfileIcon = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;

  svg {
    width: 50%;
    height: 50%;
    fill: #9ca3af;
  }
`;

const InstructorInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const InstructorName = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 6px;
  }
`;

const InstructorSpecialty = styled.div`
  font-size: 15px;
  color: ${claudeTokens.color.text2};
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const InstructorRating = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  font-size: 15px;
  color: #1e293b;

  @media (max-width: 768px) {
    margin-top: 10px;
    font-size: 14px;
  }
`;

const InstructorRatingBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #fff7e6;
  color: #b45309;
  font-weight: 600;
  border-radius: 16px;
  font-size: 14px;

  @media (max-width: 768px) {
    font-size: 13px;
    padding: 3px 8px;
  }
`;

// 후기가 아직 없는 강사용 뱃지 — 별점 뱃지와 달리 중립 톤으로 두어 "낮은 평점"으로 읽히지 않게 한다.
const InstructorNoReviewBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: ${claudeTokens.color.bgSunken};
  color: ${claudeTokens.color.text3};
  font-weight: 500;
  border-radius: 16px;
  font-size: 14px;

  @media (max-width: 768px) {
    font-size: 13px;
    padding: 3px 8px;
  }
`;


const SeminarDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [seminar, setSeminar] = useState<Seminar | null>(null);
  // 상시 동영상 판매 종료·재개 처리 중
  const [togglingSales, setTogglingSales] = useState(false);
  // 세미나 결제 기능 ON/OFF — OFF면 유료 세미나 신청 버튼을 숨긴다(무료 신청 방지)
  const [paymentEnabled, setPaymentEnabled] = useState(false);
  // 결제 설정(/points/features) 조회 실패 — 유료 세미나 신청을 막는다(무료 신청으로 넘어가지 않게)
  const [flagsFailed, setFlagsFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // 상세를 못 받았을 때 이유 — notFound(404·삭제·비공개) / failed(네트워크·서버 오류)
  const [loadError, setLoadError] = useState<'notFound' | 'failed' | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [isApplying, setIsApplying] = useState(false);
  const [, setIsCancelling] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertCallback, setAlertCallback] = useState<(() => void) | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  // 구매 확인 창 — 지금 결제하면 언제까지 몇 % 환불되는지(서버 RefundPolicy.schedule 와 같은 계산) + 확인 체크
  const [purchaseSchedule, setPurchaseSchedule] = useState<null | {
    windows: { until: string; refundRate: number; basis: string }[];
    lines: string[];
    /** 동영상 — 본인이 처음 접속한 시각 / 접속 후 환불 불가 안내(서버 문구) */
    accessedAt?: string | null;
    accessNotice?: string | null;
  }>(null);
  const [purchaseAgreed, setPurchaseAgreed] = useState(false);
  // 결제 모달 환불 일정 — 기본 접힘(첫 구간 요약만)
  const [refundOpen, setRefundOpen] = useState(false);
  // 결제 모달에서 고른 쿠폰(없으면 null) — 결제 금액 = 참가비 − 할인, prepare 에 couponIssueId 로 넘긴다
  const [purchaseCoupon, setPurchaseCoupon] = useState<SelectedPurchaseCoupon | null>(null);
  // 쿠폰 목록을 불러오는 동안엔 결제 버튼을 막는다(자동 선택 전 정가로 결제되지 않게)
  const [purchaseCouponsLoading, setPurchaseCouponsLoading] = useState(false);
  // 유료 세미나 취소·환불 모달 (환불 견적 포함)
  // 상세의 취소 팝업은 신청 내역(SeminarCancelDialog)으로 옮겨 여기서는 견적을 채우지 않는다
  const [refundQuote] = useState<null | {
    hasPayment: boolean;
    cancellable?: boolean;
    refundRate?: number;
    originalAmount?: number;
    refundAmount?: number;
    reason?: string;
    // 쿠폰 결제면(서버가 내려주면) — 취소 팝업에 "정가 − 쿠폰"·복원 여부 한 줄
    coupon?: RefundQuoteCoupon | null;
  }>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmCallback, setConfirmCallback] = useState<(() => void) | null>(null);
  const isMobile = useIsMobile();
  // 상세 레이아웃 전환 기준 — 하단 탭이 보이는 폭(≤768)이면 모바일(정보 표 + 하단 고정 바), 넘으면 PC 2단
  const isMobileLayout = useMediaQuery(MOBILE_QUERY);
  const setToast = useSetRecoilState(toastState);
  const [user, setUser] = useRecoilState(userAtom);
  const [reviewContent, setReviewContent] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [editingReviewId, setEditingReviewId] = useState<string | null>(null);
  const [editingReviewContent, setEditingReviewContent] = useState('');
  const [editingReviewRating, setEditingReviewRating] = useState(5);
  const [isUpdatingReview, setIsUpdatingReview] = useState(false);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [activeReplyReviewId, setActiveReplyReviewId] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [openReviewMenuId, setOpenReviewMenuId] = useState<string | null>(null);
  const [openReplyMenuId, setOpenReplyMenuId] = useState<string | null>(null);
  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);
  const [editingReplyContent, setEditingReplyContent] = useState('');
  const [isUpdatingReply, setIsUpdatingReply] = useState(false);

  const [searchParams] = useSearchParams();
  const couponCodeClaimedRef = React.useRef(false);

  // 쿠폰 — 커리어 유료 + 결제 기능 ON 일 때만. API 가 아직 없으면 빈 목록(쿠폰 없음)으로 폴백
  const listPrice: number = seminar?.participationFee ?? seminar?.price ?? 0;
  // 쿠폰은 지금 신청할 수 있는 세미나(공개·모집 중·시작 전·정원 남음·미폐강)이고 본인이 아직 신청하지 않았을 때만.
  // 아니면 쿠폰 카드·쿠폰 적용가를 모두 숨긴다(서버도 같은 조건으로 빈 목록을 주지만 화면에서도 막는다)
  const couponOpen: boolean = (() => {
    if (!seminar || seminar.status !== 'PUBLISHED' || seminar.isApplied) return false;
    const nowMs: number = Date.now();
    const at = (value?: string): number | null => {
      if (!value) return null;
      const t: number = new Date(value).getTime();
      return Number.isNaN(t) ? null : t;
    };
    const recruitStart: number | null = at(seminar.recruitmentStartDateTime);
    const recruitEnd: number | null = at(seminar.recruitmentEndDateTime);
    const start: number | null = at(seminar.startDateTime);
    if (seminar.isRecruitmentClosed) return false;
    if (recruitStart !== null && recruitStart > nowMs) return false;
    if (recruitEnd !== null && recruitEnd < nowMs) return false;
    // 상시 동영상은 판매 시작 뒤에도 판다(판매 종료면 막힘)
    const open: boolean = isOpenSchedule(seminar);
    if (seminar.salesClosed) return false;
    if (!open && start !== null && start <= nowMs) return false;
    const max: number = seminar.maxParticipants || seminar.capacity || 0;
    const full: boolean = seminar.isFull ?? (max > 0 && seminar.currentParticipants >= max);
    return !full;
  })();
  const couponEnabled: boolean = seminar?.seminarType === 'CAREER' && listPrice > 0 && paymentEnabled && couponOpen;
  const { coupons: claimableCoupons, best: bestCoupon, claim: claimCoupon, claimingId, refresh: refreshCoupons, claimedStateOf } = useSeminarCoupons({
    seminarId: id,
    enabled: couponEnabled,
    isLoggedIn: Boolean(user?.no),
    listPrice,
  });

  // 공유 링크 ?coupon=CODE — 로그인했으면 자동으로 받기(쿠폰함에 넣기)
  useEffect(() => {
    const code: string | null = searchParams.get('coupon');
    if (!code || !couponEnabled || couponCodeClaimedRef.current) return;
    couponCodeClaimedRef.current = true;
    if (!user?.no) {
      setToast({ show: true, message: '로그인하면 쿠폰을 받을 수 있어요.', type: 'info' });
      return;
    }
    // 같은 코드는 이 탭(세션)에서 한 번만 등록을 시도한다 — 새로고침·뒤로가기마다 토스트가 뜨지 않게
    const triedKey: string = `seminarCouponCodeTried:${code.toUpperCase()}`;
    try {
      if (window.sessionStorage.getItem(triedKey)) return;
      window.sessionStorage.setItem(triedKey, '1');
    } catch {
      // 저장소를 못 쓰면 이번 화면에서만 한 번(ref)
    }
    const isAlreadyClaimed = (message?: string | null): boolean => /이미 받은/.test(message ?? '');
    api.coupon.claimByCode(code)
      .then((res) => {
        if (res?.code === 0) {
          setToast({ show: true, message: '쿠폰을 받았어요.', type: 'success' });
          notifyCouponCountChanged();
        } else if (!isAlreadyClaimed(res?.message)) {
          setToast({ show: true, message: res?.message || '쿠폰을 받지 못했어요.', type: 'error' });
        }
        void refreshCoupons();
      })
      .catch((error: unknown) => {
        const message: string = errorMessageOf(error, '쿠폰을 받지 못했어요.');
        if (!isAlreadyClaimed(message)) setToast({ show: true, message, type: 'error' });
      });
  }, [searchParams, couponEnabled, user?.no, refreshCoupons, setToast]);

  // 이미 신청(결제)한 세미나 — 쿠폰 적용가 대신 실제 결제한 금액(환불 견적의 결제 금액·쿠폰 정보)
  const [paidPrice, setPaidPrice] = useState<PaidPrice | null>(null);
  const alreadyApplied: boolean = Boolean(seminar?.isApplied);
  useEffect(() => {
    if (!id || !alreadyApplied || !user?.no || listPrice <= 0) {
      setPaidPrice(null);
      return;
    }
    let alive: boolean = true;
    api.seminar.refundQuote(id)
      .then((res) => {
        const q = res?.result;
        if (!alive || !q?.hasPayment || typeof q.originalAmount !== 'number') return;
        setPaidPrice({
          amount: q.originalAmount,
          listAmount: q.coupon?.listAmount ?? listPrice,
          discountAmount: q.coupon?.discountAmount ?? 0,
        });
      })
      .catch(() => { /* 조회 실패 — 정가만 보인다 */ });
    return () => { alive = false; };
  }, [id, alreadyApplied, user?.no, listPrice]);
  // 신청한 세미나에는 '쿠폰 적용가'를 보이지 않는다(결제 금액이 있으면 그 금액, 없으면 정가)
  const displayCoupon = alreadyApplied ? null : bestCoupon;

  const handleClaimCoupon = async (coupon: SeminarClaimableCoupon): Promise<void> => {
    if (!user?.no) {
      navigate(`/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }
    const failure: string | null = await claimCoupon(coupon.couponId);
    setToast(failure
      ? { show: true, message: failure, type: 'error' }
      : { show: true, message: `${coupon.name} 쿠폰을 받았어요.`, type: 'success' });
  };

  useEffect(() => {
    if (user?.no) return;
    const cachedProfile = profileCache.get();
    if (cachedProfile) {
      setUser(cachedProfile);
    }
  }, [user?.no, setUser]);

  // 세미나 결제 기능 플래그 조회 (신청 버튼 노출 판단용)
  const loadFeatureFlags = (): void => {
    api.point.getFeatureFlags()
      .then((flags: any) => {
        const ok: boolean = flags?.code === 0 || flags?.result != null;
        setFlagsFailed(!ok);
        setPaymentEnabled(ok && !!flags?.result?.seminarPayment?.enabled);
      })
      .catch(() => {
        setFlagsFailed(true);
        setPaymentEnabled(false);
      });
  };
  useEffect(() => {
    loadFeatureFlags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchSeminar = async () => {
      if (!id) return;
      setLoadError(null);

      try {
        const response = await api.seminar.getDetail(id);

        if (response.code === 0) {
          const seminarData = response.result;
          setSeminar(seminarData);
        } else {
          console.error('세미나 조회 실패:', response.message);
          setSeminar(null);
          setLoadError('notFound');
        }
      } catch (error: unknown) {
        console.error('세미나 정보 가져오기 실패:', error);
        setSeminar(null);
        const status: number | undefined = (error as { response?: { status?: number } })?.response?.status;
        setLoadError(status === 404 || status === 403 ? 'notFound' : 'failed');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSeminar();
  }, [id, reloadKey]);

  const showAlert = (message: string, callback?: () => void) => {
    setAlertMessage(message);
    setShowAlertModal(true);
    setAlertCallback(() => callback || null);
  };

  const closeAlert = () => {
    setShowAlertModal(false);
    if (alertCallback) {
      alertCallback();
      setAlertCallback(null);
    }
  };

  const showConfirm = (message: string, callback: () => void) => {
    setConfirmMessage(message);
    setShowConfirmModal(true);
    setConfirmCallback(() => callback);
  };

  const closeConfirm = () => {
    setShowConfirmModal(false);
    setConfirmCallback(null);
  };

  const handleConfirm = () => {
    setShowConfirmModal(false);
    if (confirmCallback) {
      confirmCallback();
      setConfirmCallback(null);
    }
  };

  // 유료 세미나: 주문 생성 → 토스 결제창형 결제(주문서형/결제창형 = 구 결제위젯) → successUrl에서 승인(confirm)
  // ※ 결제창(구버전, tossPayments.payment())이 아니라 결제창형(widgets.renderPaymentWindow)을 쓴다.
  //   토스 계약/카드사 심사가 신버전(주문서형·결제창형) 연동을 요구하고, 키도 '주문서형·결제창형 연동 키'(gck/gsk)를 써야 한다.
  const startPaidApplication = async (coupon: SelectedPurchaseCoupon | null) => {
    if (!id) return;
    setIsApplying(true);
    try {
      const prep = await api.seminar.paymentPrepare(id, coupon?.issueId ?? null);
      if (prep.code !== 0 || !prep.result) {
        showAlert(prep.message || '결제 준비에 실패했습니다.');
        return;
      }
      const expected: number = Math.max(0, listPrice - (coupon?.discountAmount ?? 0));
      if (prep.result.amount !== expected) {
        // 결제 금액은 서버가 정한 값(prep.amount)만 쓴다 — 모달에서 본 금액과 다르면 먼저 알린다
        const result: SeminarPaymentPrepareResult = prep.result;
        showConfirm(
          `결제 금액이 ${won(result.amount)}이에요.\n쿠폰 할인이 바뀌었어요.\n이 금액으로 결제할까요?`,
          () => { void openPaymentWindow(result); },
        );
        return;
      }
      await openPaymentWindow(prep.result);
    } catch (error: any) {
      void setPaymentMode(false);
      if (error?.code !== 'USER_CANCEL') {
        console.error('세미나 결제 시작 실패:', error);
        showAlert(error?.response?.data?.message || error?.message || '결제를 시작할 수 없습니다.');
      }
    } finally {
      setIsApplying(false);
    }
  };

  /**
   * 토스 결제창 오류 문구 — 연결 실패(UNKNOWN·네트워크)는 고정 안내, 그 밖은 서버/SDK 문구를 문장마다 줄바꿈.
   */
  const paymentErrorMessage = (error: any, fallback: string): string => {
    const code: string = String(error?.code ?? '').toUpperCase();
    const raw: string = error?.response?.data?.message || error?.message || '';
    if (code === 'UNKNOWN' || code.includes('NETWORK') || /network|failed to fetch|알 수 없는 에러/i.test(raw)) {
      return '결제창 연결에 실패했어요.\n네트워크 상태를 확인하고 다시 시도해 주세요.';
    }
    return (raw || fallback).replace(/([.?!])\s+/g, '$1\n');
  };

  /**
   * 결제창을 먼저 닫고(얼럿이 결제창 뒤에 깔리지 않게) 결제모드를 해제한 뒤 알린다.
   * 사용자가 닫은 경우(USER_CANCEL)는 조용히 둔다.
   */
  const failPayment = async (error: any, paymentWindow: { destroy: () => Promise<void> } | null, fallback: string): Promise<void> => {
    if (paymentWindow) await paymentWindow.destroy().catch(() => undefined);
    void setPaymentMode(false);
    if (error?.code === 'USER_CANCEL') return;
    console.error('세미나 결제 실패:', error);
    showAlert(paymentErrorMessage(error, fallback));
  };

  // 서버가 고정한 금액(prep.amount)으로 토스 결제창을 연다
  const openPaymentWindow = async (prep: SeminarPaymentPrepareResult) => {
    if (!id) return;
    setIsApplying(true);
    let paymentWindow: { destroy: () => Promise<void> } | null = null;
    try {
      const { orderId, amount, seminarTitle } = prep;
      // 결제 시작 — 노출→상세→결제 퍼널(결제 완료는 기존 SEMINAR_PAYMENT)
      track.click({ screen: UI_SCREEN.SEMINAR_DETAIL }, { kind: UI_KIND.SEMINAR, id: String(id) }, { action: 'pay_start' });
      // 결제 완료 화면이 "정가 − 쿠폰 = 결제"를 그릴 수 있게 주문별로 잠깐 남겨 둔다(서버 confirm 응답에 없을 때 폴백)
      stashPaymentCoupon(orderId, {
        listAmount: prep.listAmount ?? (listPrice || amount),
        discountAmount: prep.discountAmount ?? 0,
        couponName: prep.couponName ?? null,
      });
      const toss = await loadTossPayments(import.meta.env.VITE_TOSS_CLIENT_KEY);
      const widgets = toss.widgets({ customerKey: ANONYMOUS });
      // 결제창을 띄우기 전에 서버가 고정한 금액을 설정 — successUrl 의 amount 와 대조해 서버(confirm)에서 재검증한다
      await widgets.setAmount({ currency: 'KRW', value: amount });
      const rendered = await widgets.renderPaymentWindow({
        variantKey: { paymentMethod: 'DEFAULT', agreement: 'AGREEMENT' },
      });
      paymentWindow = rendered;
      // 구매자가 결제창에서 결제수단을 고르면 paymentRequest 이벤트 → 실제 결제 요청(리다이렉트)
      rendered.on('paymentRequest', async () => {
        try {
          // 결제 요청 직전에만 켠다 — 앱에서는 이 구간의 http(s) 이동이 전부 웹뷰 안에 붙잡힌다(사파리 이탈 방지).
          await setPaymentMode(true);
          await widgets.requestPayment({
            orderId,
            orderName: (seminarTitle || '세미나 참가비').slice(0, 100),
            successUrl: `${window.location.origin}/seminars/${id}/payment/success`,
            failUrl: `${window.location.origin}/seminars/${id}/payment/fail`,
            customerEmail: user?.email || undefined,
            customerName: user?.name || undefined,
          });
        } catch (error: any) {
          // 사용자가 결제창을 닫으면 USER_CANCEL — 조용히 무시. 그 밖은 결제창을 닫고(destroy) 결제모드 해제 후 알린다
          await failPayment(error, error?.code === 'USER_CANCEL' ? null : rendered, '결제를 진행할 수 없어요.');
        }
      });
    } catch (error: any) {
      // setAmount·renderPaymentWindow 실패도 같은 처리 — 떠 있는 결제창이 있으면 먼저 닫는다
      await failPayment(error, paymentWindow, '결제를 시작할 수 없어요.');
    } finally {
      // 주의: 여기서 setPaymentMode(false) 를 부르지 않는다.
      // requestPayment 는 페이지 이동(성공 경로)을 유발하므로, finally 는 결제 요청 직후 곧바로 실행돼
      // 켠 직후 바로 꺼버리는 레이스가 생긴다. OFF는 예외 경로(위 두 catch)에서만 한다.
      setIsApplying(false);
    }
  };

  const handleApply = async () => {
    if (!id || !canApply) return;
    setPurchaseCoupon(null);

    // 유료 세미나 + 결제 기능 ON이면 결제 플로우로 (OFF면 기존 무료 신청 그대로)
    const fee: number = listPrice;
    if (fee > 0) {
      let flags: any = null;
      try {
        flags = await api.point.getFeatureFlags();
      } catch {
        flags = null;
      }
      // 설정을 못 불러오면 유료 세미나를 무료 신청(/apply)으로 보내지 않는다 — 막고 안내
      if (!flags || (flags.code !== 0 && flags.result == null)) {
        setFlagsFailed(true);
        showAlert('결제 설정을 불러오지 못했어요.\n잠시 후 다시 시도해 주세요.');
        return;
      }
      {
        if (flags?.result?.seminarPayment?.enabled) {
          // 결제 전 강의 정보 + 환불 일정 + 약관을 담은 구매 확인 모달
          setPurchaseAgreed(false);
          setRefundOpen(false);
          setPurchaseSchedule(null);
          setShowPurchaseModal(true);
          api.seminar.refundSchedule(id)
            .then((res) => {
              if (res.code === 0 && res.result) setPurchaseSchedule({ windows: res.result.windows, lines: res.result.lines, accessedAt: res.result.accessedAt ?? null, accessNotice: res.result.accessNotice ?? null });
            })
            .catch(() => { /* 조회 실패 시 일반 규정만 표시 */ });
          return;
        }
      }
    }

    setIsApplying(true);
    try {
      const response = await api.seminar.apply(id);

      if (response.code === 0) {
        showAlert('세미나 신청이 완료되었습니다!\n신청 내역과 신청 취소는 마이페이지 > 세미나 이력에서 할 수 있어요.');
        // 업데이트된 세미나 정보로 갱신
        setSeminar(response.result);
      } else {
        showAlert(response.message || '세미나 신청에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('세미나 신청 실패:', error);
      const errorMessage = error.response?.data?.message || '세미나 신청 중 오류가 발생했습니다.';
      showAlert(errorMessage);
    } finally {
      setIsApplying(false);
    }
  };

  const doCancel = async () => {
    if (!id) return;
    setIsCancelling(true);
    try {
      const response = await api.seminar.cancel(id);
      if (response.code === 0) {
        showAlert('세미나 신청이 취소되었습니다.');
        setSeminar(response.result);
      } else {
        showAlert(response.message || '세미나 신청 취소에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('세미나 신청 취소 실패:', error);
      const errorMessage = error.response?.data?.message || '세미나 신청 취소 중 오류가 발생했습니다.';
      showAlert(errorMessage);
    } finally {
      setIsCancelling(false);
    }
  };

  const resetReviewForm = () => {
    setReviewContent('');
    setReviewRating(5);
  };

  const resetReviewEditing = () => {
    setEditingReviewId(null);
    setEditingReviewContent('');
    setEditingReviewRating(5);
  };

  const updateSeminarState = (response: any, successMessage?: string) => {
    if (response?.code === 0) {
      setSeminar(response.result);
      if (successMessage) {
        showAlert(successMessage);
      }
      return true;
    }
    const message = response?.message || '요청 처리 중 오류가 발생했습니다.';
    showAlert(message);
    return false;
  };

  const handleReviewSubmit = async () => {
    if (!id || !seminar) return;
    if (!user) {
      showAlert('로그인 후 리뷰를 작성할 수 있습니다.');
      return;
    }
    if (!canWriteReview) {
      showAlert('세미나 참가자만 리뷰를 작성할 수 있습니다.');
      return;
    }
    if (!reviewContent.trim()) {
      showAlert('리뷰 내용을 입력해 주세요.');
      return;
    }

    setIsSubmittingReview(true);
    try {
      const payload = { content: reviewContent.trim(), rating: reviewRating };
      const response = await api.seminar.createReview(id, payload);
      const updated = updateSeminarState(response, '리뷰가 등록되었습니다.');
      if (updated) {
        resetReviewForm();
        setActiveReplyReviewId(null);
        setReplyContent('');
      }
    } catch (error: any) {
      console.error('리뷰 처리 중 오류:', error);
      const errorMessage = error.response?.data?.message || '리뷰 처리 중 오류가 발생했습니다.';
      showAlert(errorMessage);
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const startReviewEdit = (reviewId: string, content: string, rating: number) => {
    setEditingReviewId(reviewId);
    setEditingReviewContent(content);
    setEditingReviewRating(rating);
    setOpenReviewMenuId(null);
  };

  const handleReviewUpdate = async () => {
    if (!id || !editingReviewId) return;
    if (!user) {
      showAlert('로그인 후 리뷰를 수정할 수 있습니다.');
      return;
    }
    if (!editingReviewContent.trim()) {
      showAlert('리뷰 내용을 입력해 주세요.');
      return;
    }

    setIsUpdatingReview(true);
    try {
      const payload = { content: editingReviewContent.trim(), rating: editingReviewRating };
      const response = await api.seminar.updateReview(id, editingReviewId, payload);
      const updated = updateSeminarState(response, '리뷰가 수정되었습니다.');
      if (updated) {
        resetReviewEditing();
      }
    } catch (error: any) {
      console.error('리뷰 수정 실패:', error);
      const errorMessage = error.response?.data?.message || '리뷰 수정 중 오류가 발생했습니다.';
      showAlert(errorMessage);
    } finally {
      setIsUpdatingReview(false);
    }
  };

  const handleDeleteReview = (reviewId: string) => {
    if (!id) return;
    showConfirm('리뷰를 삭제하시겠습니까?', async () => {
      setIsSubmittingReview(true);
      try {
        const response = await api.seminar.deleteReview(id, reviewId);
        const deleted = updateSeminarState(response, '리뷰가 삭제되었습니다.');
        if (deleted) {
          resetReviewForm();
          if (editingReviewId === reviewId) {
            resetReviewEditing();
          }
        }
      } catch (error: any) {
        console.error('리뷰 삭제 실패:', error);
        const errorMessage = error.response?.data?.message || '리뷰 삭제 중 오류가 발생했습니다.';
        showAlert(errorMessage);
      } finally {
        setIsSubmittingReview(false);
      }
    });
  };

  const toggleReplyForm = (reviewId: string) => {
    if (!user) {
      showAlert('로그인 후 댓글을 작성할 수 있습니다.');
      return;
    }
    // 답글은 세미나 강사의 공식 답변 → 소유 강사만 작성 가능 (참가자 여부 아님)
    if (!seminar?.isAuthor) {
      showAlert('세미나 강사만 후기에 댓글을 작성할 수 있습니다.');
      return;
    }
    setReplyContent('');
    setActiveReplyReviewId(prev => (prev === reviewId ? null : reviewId));
  };

  const handleReplySubmit = async () => {
    if (!id || !activeReplyReviewId) return;
    if (!user) {
      showAlert('로그인 후 댓글을 작성할 수 있습니다.');
      return;
    }
    if (!seminar?.isAuthor) {
      showAlert('세미나 강사만 후기에 댓글을 작성할 수 있습니다.');
      return;
    }
    if (!replyContent.trim()) {
      showAlert('댓글 내용을 입력해 주세요.');
      return;
    }

    setIsSubmittingReply(true);
    try {
      const response = await api.seminar.createReviewReply(id, activeReplyReviewId, {
        content: replyContent.trim(),
      });
      const updated = updateSeminarState(response, '댓글이 등록되었습니다.');
      if (updated) {
        setReplyContent('');
        setActiveReplyReviewId(null);
      }
    } catch (error: any) {
      console.error('댓글 등록 실패:', error);
      const errorMessage = error.response?.data?.message || '댓글 등록 중 오류가 발생했습니다.';
      showAlert(errorMessage);
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const handleReplyDelete = (reviewId: string, replyId: string) => {
    if (!id) return;
    showConfirm('댓글을 삭제하시겠습니까?', async () => {
      setIsSubmittingReply(true);
      try {
        const response = await api.seminar.deleteReviewReply(id, reviewId, replyId);
        updateSeminarState(response, '댓글이 삭제되었습니다.');
        if (editingReplyId === replyId) {
          setEditingReplyId(null);
          setEditingReplyContent('');
        }
      } catch (error: any) {
        console.error('댓글 삭제 실패:', error);
        const errorMessage = error.response?.data?.message || '댓글 삭제 중 오류가 발생했습니다.';
        showAlert(errorMessage);
      } finally {
        setIsSubmittingReply(false);
      }
    });
  };

  const toggleReviewMenu = (reviewId: string) => {
    setOpenReviewMenuId(prev => (prev === reviewId ? null : reviewId));
    setOpenReplyMenuId(null);
  };

  const toggleReplyMenu = (replyId: string) => {
    setOpenReplyMenuId(prev => (prev === replyId ? null : replyId));
    setOpenReviewMenuId(null);
  };

  const startReplyEdit = (replyId: string, content: string) => {
    setEditingReplyId(replyId);
    setEditingReplyContent(content);
    setOpenReplyMenuId(null);
  };

  const resetReplyEditing = () => {
    setEditingReplyId(null);
    setEditingReplyContent('');
  };

  const handleReplyUpdate = async (reviewId: string) => {
    if (!id || !editingReplyId) return;
    if (!editingReplyContent.trim()) {
      showAlert('댓글 내용을 입력해 주세요.');
      return;
    }

    setIsUpdatingReply(true);
    try {
      const response = await api.seminar.updateReviewReply(id, reviewId, editingReplyId, {
        content: editingReplyContent.trim(),
      });
      const updated = updateSeminarState(response, '댓글이 수정되었습니다.');
      if (updated) {
        resetReplyEditing();
      }
    } catch (error: any) {
      console.error('댓글 수정 실패:', error);
      const errorMessage = error.response?.data?.message || '댓글 수정 중 오류가 발생했습니다.';
      showAlert(errorMessage);
    } finally {
      setIsUpdatingReply(false);
    }
  };

  if (isLoading) {
    return (
      <CustomPageContainer>
        <LoadingSpinner placement="page" minHeight="40vh" />
      </CustomPageContainer>
    );
  }

  if (!seminar) {
    return (
      <CustomPageContainer>
        <DetailUnavailable
          reason={loadError ?? 'notFound'}
          isLoggedIn={Boolean(user?.no)}
          onBrowse={() => navigate('/seminars')}
          onRetry={() => {
            setIsLoading(true);
            setReloadKey((key: number) => key + 1);
          }}
          onHistory={() => navigate('/seminars/history')}
          onLogin={() => navigate(`/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
          onOpenSeminar={(seminarId: string | number) => navigate(`/seminars/${seminarId}`)}
          currentId={id}
        />
      </CustomPageContainer>
    );
  }

  // 세미나 유형 — CAREER(명치사 커리어)만 리뷰/강사정보/신청 등 전체 기능 노출.
  // 미지정/그 외는 일반(GENERAL)로 간주해 기본 카드 정보만 노출 (레거시 안전)
  const isCareer = seminar.seminarType === 'CAREER';
  const maxParticipants = seminar.maxParticipants || seminar.capacity || 0;
  // 정원 마감은 백엔드 isFull(승인 대기 결제 포함 점유 기준) 우선 사용 — 백엔드 신청 차단 로직과 일치.
  // (구버전 응답 폴백: 승인자 수 기준)
  const isFullyBooked = seminar.isFull ?? (seminar.currentParticipants >= maxParticipants);
  const participantStatus = seminar.participantStatus?.toUpperCase?.() || null;
  const isApplied = Boolean(seminar.isApplied);
  const isPendingApproval = participantStatus === 'PENDING';
  const isApprovedParticipant = participantStatus === 'APPLIED' || participantStatus === 'ATTENDED';
  const isRejected = participantStatus === 'REJECTED';
  // 거절·참석완료가 아닌 신청 상태만 취소 가능 (참석완료(ATTENDED) 후에는 취소 불가 → 버튼 미노출)
  const recruitmentStart = seminar.recruitmentStartDateTime ? new Date(seminar.recruitmentStartDateTime) : null;
  const recruitmentEnd = seminar.recruitmentEndDateTime ? new Date(seminar.recruitmentEndDateTime) : null;
  const now = new Date();
  const recruitmentNotStarted = recruitmentStart ? recruitmentStart.getTime() > now.getTime() : false;
  const recruitmentEnded =
    Boolean(seminar.isRecruitmentClosed) ||
    (recruitmentEnd ? recruitmentEnd.getTime() < now.getTime() : false);
  const hasSeminarStarted = seminar.startDateTime ? new Date(seminar.startDateTime).getTime() <= now.getTime() : false;
  // 상시 동영상(OPEN)은 startDateTime 이 판매 시작이라 시작 후에도 신청(구매)을 받는다. 판매 종료(salesClosed)면 막힌다.
  const isOpenVod: boolean = isOpenSchedule(seminar);
  const salesClosed: boolean = seminar.salesClosed === true;
  const applyClosedByStart: boolean = hasSeminarStarted && !isOpenVod;
  const canApply =
    seminar.status === 'PUBLISHED' &&
    !salesClosed &&
    !isFullyBooked &&
    !isApplied &&
    !recruitmentEnded &&
    !recruitmentNotStarted;
  // 우하단 플로팅: 신청 독려 CTA(신청 가능 시) / 신청 완료 상태(이미 신청 시). 작성자는 미노출.
  const showFloatingApply = isCareer && !seminar.isAuthor && paymentEnabled && canApply && !applyClosedByStart;
  const showFloatingApplied = isCareer && !seminar.isAuthor && seminar.status === 'PUBLISHED' && isApplied && !isRejected;
  // 하단 액션영역: 작성자 수정 / 신청취소 / 모집전 안내만 남김(신청 버튼은 플로팅으로 이동). 내용 없으면 숨김.
  const showActionSection = seminar.isAuthor || (isCareer && recruitmentNotStarted);
  const reviews = seminar.reviews ?? [];
  const averageRating = seminar.averageRating ?? 0;
  const reviewCount = seminar.reviewCount ?? reviews.length;
  const canWriteReview = isApprovedParticipant;
  // 이미 후기를 쓴 사용자는 작성 칸 대신 한 줄 안내(한 세미나에 후기 1개)
  const hasMyReview: boolean = reviews.some((review) => review.isAuthor && !review.isDeleted);
  // 서버 판단(reviewEligibility)이 오면 그 값을 먼저 — 불가면 작성 칸 대신 사유 한 줄. 없으면 기존 규칙(승인 참가자·시작 후)
  const reviewEligibility = seminar.reviewEligibility ?? null;
  const reviewBlockedReason: string | null = user && reviewEligibility && !reviewEligibility.canWrite && !hasMyReview
    ? (reviewEligibility.reason || '지금은 후기를 쓸 수 없어요.')
    : null;
  const canSubmitReview = Boolean(user && !hasMyReview && (reviewEligibility
    ? reviewEligibility.canWrite
    : canWriteReview && hasSeminarStarted));
  const reviewPlaceholder = !user
    ? '로그인 후 리뷰를 작성할 수 있습니다.'
    : !isApprovedParticipant
      ? '승인된 참가자만 리뷰를 작성할 수 있습니다.'
      : !hasSeminarStarted
        ? '세미나 시작 이후에 리뷰를 작성할 수 있습니다.'
        : '세미나 리뷰를 입력해 주세요. (최대 2,000자)';
  const normalizedLocation: Exclude<Seminar['location'], string> =
    typeof seminar.location === 'string'
      ? {
          address: seminar.location,
          detailAddress: '',
          fullAddress: seminar.location,
          coordinates: undefined,
          nearbyStations: [],
        }
      : seminar.location;
  const hasNearbyStations = normalizedLocation?.nearbyStations?.length > 0;
  const hasLocationSection =
    Boolean(seminar.address || normalizedLocation?.fullAddress || hasNearbyStations);

  // 폐강(CANCELLED)된 세미나만 상세 접근을 막는다. 모집종료/시작전/마감이어도 세미나 자체는 열어둔다:
  // 사람들이 세미나를 보고(과거·예정 포함) 관심을 갖고 신청하도록 유도하고, 참가자는 리뷰 작성/취소·환불,
  // 시작 이후 리뷰 열람이 가능해야 한다. (신청 버튼은 상태에 따라 '모집 전/모집 마감/정원 마감'으로 비활성 처리됨)
  const isCancelledSeminar: boolean = seminar.status === 'CANCELLED';
  if (isCancelledSeminar) {
    return (
      <ResultModal
        open={true}
        status={false}
        title="세미나 안내"
        description={'폐강된 세미나입니다.'}
        buttontext="홈으로"
        onClose={() => navigate('/')}
        onClick={() => navigate('/')}
      />
    );
  }

  const format = formatOf(seminar);
  // 동영상 참가자가 아직 안 열었으면 url 은 null(POST …/online-access/open 응답으로만 온다) — 카드는 onlineAccess 가 있으면 보인다
  const onlineAccess = seminar.onlineAccess ?? null;
  const info = buildDetailInfo(seminar, {
    recruitmentNotStarted,
    recruitmentEnded,
    isFullyBooked,
    hasOnlineAccess: Boolean(onlineAccess),
    accessed: Boolean(onlineAccess?.accessedAt),
  });
  // 오프라인(커리어 오프라인·일반 오프라인)만 지도·주소 카드
  const showLocationCard: boolean = hasLocationSection && format === 'OFFLINE' && !isGeneralOnline(seminar);
  const mapSearchAddress: string | undefined = normalizedLocation?.address || seminar.address || normalizedLocation?.fullAddress || undefined;
  const instructorProfile = seminar.instructorProfile;

  // 신청 버튼(모바일 하단 바 · PC 요약 카드 공용). 작성자·일반 세미나는 없음.
  const applyAction: ApplyActionState | null = (() => {
    if (!isCareer || seminar.isAuthor) return null;
    // 신청한 뒤에도 갈 곳이 있어야 한다 — 비활성 버튼은 막다른 길이라 신청내역으로 보낸다
    if (showFloatingApplied) {
      return {
        mode: 'applied',
        label: isPendingApproval ? '승인 대기 · 내역' : '신청 완료 · 내역',
        onClick: () => navigate('/seminars/history'),
        // 링크·취소는 상세에 두지 않는다 — 세미나 이력에서(2026-09-28 사용자 결정)
        hint: `${!isPendingApproval && format === 'VOD' ? '시청 링크·' : !isPendingApproval && format === 'LIVE' ? '접속 링크·' : ''}신청 취소는 마이페이지 > 세미나 이력에서`,
      };
    }
    if (showFloatingApply) {
      return { mode: 'apply', label: isApplying ? <ButtonSpinner /> : '신청하기', onClick: handleApply, busy: isApplying };
    }
    // 결제 설정 조회 실패 — 신청은 막고, 누르면 안내하고 설정을 다시 불러온다
    if (flagsFailed && canApply && !applyClosedByStart && listPrice > 0) {
      return {
        mode: 'disabled',
        label: '신청하기',
        onClick: () => {
          showAlert('결제 설정을 불러오지 못했어요.\n잠시 후 다시 시도해 주세요.');
          loadFeatureFlags();
        },
      };
    }
    if (seminar.status === 'PUBLISHED' && !applyClosedByStart && !isApplied) {
      if (salesClosed) return { mode: 'disabled', label: '판매 종료' };
      if (recruitmentNotStarted) return { mode: 'disabled', label: '모집 전' };
      if (recruitmentEnded) return { mode: 'disabled', label: '모집 마감' };
      if (isFullyBooked) return { mode: 'disabled', label: '정원 마감' };
    }
    return null;
  })();

  // 강사 본인 — 상시 동영상 판매 종료·재개. 이미 결제한 참가자는 계속 볼 수 있다
  const handleToggleSales = (): void => {
    const closing: boolean = !salesClosed;
    showConfirm(
      closing
        ? '판매를 멈출까요?\n목록에서 빠지고 새로 결제할 수 없어요.\n이미 결제한 분은 계속 볼 수 있어요.'
        : '판매를 다시 시작할까요?\n목록에 다시 보이고 결제할 수 있어요.',
      async () => {
        setTogglingSales(true);
        try {
          const res = await api.seminar.setSalesClosed(String(seminar.id), closing);
          if (res?.code !== 0) {
            showAlert(res?.message || '판매 상태를 바꾸지 못했어요.');
            return;
          }
          // 서버가 상세를 돌려주면 그대로, 아니면 판매 종료 값만 바꾼다
          const next: Seminar | null = res.result && typeof res.result === 'object' && 'id' in res.result ? (res.result as Seminar) : null;
          setSeminar((prev) => (next ? { ...prev, ...next, salesClosed: next.salesClosed ?? closing } as Seminar : prev ? { ...prev, salesClosed: closing } : prev));
          setToast({ show: true, message: closing ? '판매를 멈췄어요.' : '판매를 다시 시작했어요.', type: 'success' });
        } catch (error: any) {
          showAlert(error?.response?.data?.message || '판매 상태를 바꾸지 못했어요.\n잠시 후 다시 시도해 주세요.');
        } finally {
          setTogglingSales(false);
        }
      },
    );
  };

  // PC 요약 카드 맨 아래 안내(문장마다 줄바꿈).
  // "진행 중 폐강 전액 환불"은 진행 기간이 있는 세미나(기간·회차·동영상)에만 해당한다 — 당일 세미나엔 '진행 중'이 없다
  const hasRunningPeriod: boolean = format === 'VOD' || seminar.schedule === 'PERIOD' || seminar.schedule === 'SESSIONS';
  // 동영상은 "접속 링크를 열면 환불할 수 없어요." 를 맨 아래에(상시는 결제 후 7일·접속 전 규칙을 앞에)
  const vodNotes: string[] = isCareer && info.fee > 0
    ? (onlineAccess?.accessedAt && format === 'VOD' ? ['접속 링크를 열어 환불할 수 없어요.'] : vodRefundNotes(seminar))
    : [];
  const summaryNotes: string[] = !isCareer
    ? GENERAL_NOTICE_LINES
    : info.fee > 0
      ? [
          ...(isOpenVod ? [] : ['결제 후 7일 이내·시작 전 취소는 전액 환불돼요.']),
          ...(isOpenVod
            ? ['판매 중 폐강되면 이미 결제한 분 모두 전액 환불돼요.']
            : hasRunningPeriod ? ['폐강되면 진행 중이어도 전액 환불돼요.'] : []),
          ...(isOpenVod ? ['판매가 끝나도 이미 결제한 분은 계속 볼 수 있어요.'] : []),
          ...vodNotes,
        ]
      : ['신청 내역은 마이페이지에서 확인할 수 있어요.'];

  // 결제 모달 환불 일정 — 서버 lines 중 "기한: 비율" 줄은 표, 나머지 문장(상시 규칙·접속 후 환불 불가)은 안내로
  const scheduleLines: string[] = purchaseSchedule?.lines ?? [];
  const scheduleWindowLines: string[] = scheduleLines.filter((line: string) => line.includes(': '));
  const scheduleNoteLines: string[] = [
    ...scheduleLines.filter((line: string) => !line.includes(': ')),
    ...(purchaseSchedule?.accessNotice && !scheduleLines.includes(purchaseSchedule.accessNotice) ? [purchaseSchedule.accessNotice] : []),
  ];
  // 접힌 요약 — 첫 구간만("10/8까지 100% 환불"), 일정이 없으면 규정 첫 줄
  const firstWindow = purchaseSchedule?.windows?.[0];
  const refundSummary: string | null = firstWindow?.until
    ? `${formatMonthDay(firstWindow.until).replace(/\(.\)$/, '')}까지 ${firstWindow.refundRate}% 환불`
    : isOpenVod ? null : REFUND_POLICY_RULES[0]?.text ?? null;
  const purchaseNoteLines: string[] = [
    ...scheduleNoteLines,
    ...vodNotes.filter((line: string) => !scheduleNoteLines.includes(line)),
  ];

  // 결제 모달 금액 — 참가비 − 선택한 쿠폰 할인(서버 applicable 이 계산한 값)
  const purchaseAmount: number = Math.max(0, listPrice - (purchaseCoupon?.discountAmount ?? 0));

  // 공유 — 버튼은 PC 요약 카드에만 있다. 기기 공유 창(맥의 AirDrop·Mail 메뉴) 없이 링크 복사(채용 상세와 같다, 2026-10-01).
  // 주소에 붙는 UTM 은 헤더 공유 버튼과 같은 규약(common/shareLink)
  const handleShare = async (): Promise<void> => {
    const result: ShareResult = await shareCurrentPage(seminar.title, false);
    if (result === 'copied') setToast({ show: true, message: '링크가 복사되었습니다.', type: 'success' });
    if (result === 'failed') setToast({ show: true, message: '링크 복사에 실패했어요.', type: 'error' });
  };

  return (
    <CustomPageContainer>
      <DetailPage>
        <MainColumn>
          <DetailHero
            imageUrl={seminar.thumbnailUrl || seminar.thumbnail}
            statusBadge={statusBadgeOf(seminar)}
          />

          {/* 거절 알림 */}
          {isRejected && (
            <AlertBox variant="error">
              <AlertIcon>⚠️</AlertIcon>
              <AlertContent>
                <AlertTitle>세미나 신청이 거절되었습니다</AlertTitle>
                {seminar.rejectionReason && (
                  <AlertMessage>{seminar.rejectionReason}</AlertMessage>
                )}
              </AlertContent>
            </AlertBox>
          )}

          {/* 승인 대기 알림 */}
          {isPendingApproval && (
            <AlertBox variant="info">
              <AlertIcon>⏳</AlertIcon>
              <AlertContent>
                <AlertTitle>승인 대기 중입니다</AlertTitle>
                <AlertMessage>강사 승인 후 참석이 확정됩니다.</AlertMessage>
              </AlertContent>
            </AlertBox>
          )}

          <DetailTitleCard
            title={seminar.title}
            // 일반(외부) 세미나는 분야·대상을 입력받지 않으므로 칩을 숨긴다
            chips={isCareer ? (seminar.categories?.map(cat => cat.displayName) ?? (seminar.category ? [seminar.category] : [])) : []}
            levelChip={isCareer ? (typeof seminar.level === 'object' ? seminar.level?.displayName : seminar.level) : undefined}
            instructorName={seminar.instructorNickname || seminar.instructor || ''}
            instructorImageUrl={isCareer ? instructorProfile?.profileImageUrl : undefined}
            onInstructorClick={isCareer && instructorProfile ? () => navigate(`/instructors/${instructorProfile.id}`) : undefined}
          />

          {isMobileLayout && <DetailInfoCard info={info} couponPrice={displayCoupon} paidPrice={paidPrice} />}

          {/* 쿠폰 N장 — 정보 표 아래(PC 는 제목 카드 아래) */}
          {couponEnabled && (
            <SeminarCouponCard coupons={claimableCoupons} listPrice={listPrice} claimingId={claimingId} onClaim={handleClaimCoupon} claimedStateOf={claimedStateOf} />
          )}

          {/* PC 는 요약 카드에 일정 한 줄만 있으니 회차 목록은 본문에 따로 */}
          {!isMobileLayout && info.sessions.length > 0 && (
            <DCard aria-labelledby="seminar-sessions-title">
              <DCardTitle id="seminar-sessions-title">회차 일정</DCardTitle>
              <SessionList sessions={info.sessions} />
            </DCard>
          )}


          <DCard aria-labelledby="seminar-description-title">
            <DCardTitle id="seminar-description-title">세미나 설명</DCardTitle>
            <CollapsibleDescription html={seminar.description || ''} foldHeight={isMobileLayout ? DESCRIPTION_FOLD_HEIGHT.mobile : DESCRIPTION_FOLD_HEIGHT.pc} />
          </DCard>

          {showLocationCard && (
            <DCard aria-labelledby="seminar-location-title">
              <DCardTitle>
                <span id="seminar-location-title">위치 정보</span>
                {/* 지도는 고정이라 자세히 볼 사람은 네이버 지도로 — 검색은 도로명 주소(상세 장소명이 붙으면 빗나간다) */}
                {mapSearchAddress && (
                  <DCardTitleLink
                    href={`https://map.naver.com/p/search/${encodeURIComponent(mapSearchAddress)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ChevronLabel>지도에서 보기</ChevronLabel>
                  </DCardTitleLink>
                )}
              </DCardTitle>
              {(seminar.latitude || normalizedLocation?.coordinates?.latitude) && (
                <NaverMap
                  x={seminar.latitude || normalizedLocation?.coordinates?.latitude || 0}
                  y={seminar.longitude || normalizedLocation?.coordinates?.longitude || 0}
                  chipList={normalizedLocation?.nearbyStations?.map((station, index) => ({
                    id: String(index),
                    name: `${station.stationName} (${station.line})`,
                    lineNo: station.line,
                  })) || []}
                  // 상세 지도는 고정(드래그·휠·핀치 없음) — 세로 스크롤이 페이지로 넘어가게, 자세히는 제목 줄 "지도에서 보기"
                  fixed
                />
              )}
              {(normalizedLocation?.address || seminar.address || normalizedLocation?.fullAddress) && (
                <LocationAddressGrid labelWidth={76}>
                  <dt>주소</dt>
                  {/* 정보 표 "장소" 행과 같은 방식 — 도로명 + 상세 장소명을 한 문장으로(자연 줄바꿈) */}
                  <dd>
                    {normalizedLocation?.fullAddress
                      || [normalizedLocation?.address || seminar.address, normalizedLocation?.detailAddress].filter(Boolean).join(' ')}
                  </dd>
                </LocationAddressGrid>
              )}
            </DCard>
          )}

      {isCareer && instructorProfile && (
        <InstructorProfileSection>
          <DCardTitle>강사 소개</DCardTitle>
          <InstructorProfileCard onClick={() => navigate(`/instructors/${instructorProfile.id}`)}>
            <InstructorThumbnail>
              {instructorProfile.profileImageUrl ? (
                <InstructorThumbnailImage src={instructorProfile.profileImageUrl} alt={instructorProfile.name} />
              ) : (
                <DefaultProfileIcon>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695A18.683 18.683 0 0112 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 01-.437-.695z" clipRule="evenodd" />
                  </svg>
                </DefaultProfileIcon>
              )}
            </InstructorThumbnail>
            <InstructorInfo>
              <InstructorName>{instructorProfile.name}</InstructorName>
              {instructorProfile.specialties && instructorProfile.specialties.length > 0 && (
                <InstructorSpecialty>
                  {instructorProfile.specialties.map(s => s.displayName).join(' · ')}
                </InstructorSpecialty>
              )}
              <InstructorRating>
                {(instructorProfile.reviewCount ?? 0) > 0 ? (
                  <InstructorRatingBadge>
                    <IconLabel start="star" size={16} gap={4}>{(instructorProfile.rating ?? 0).toFixed(1)} · 후기 {instructorProfile.reviewCount}개</IconLabel>
                  </InstructorRatingBadge>
                ) : (
                  <InstructorNoReviewBadge>후기 없음</InstructorNoReviewBadge>
                )}
              </InstructorRating>
            </InstructorInfo>
          </InstructorProfileCard>
        </InstructorProfileSection>
      )}

      {isCareer && (
      <ReviewSection>
        <ReviewHeader>
          <Text size="1.2rem" fontweight="bold" margin="0">
            리뷰 ({reviewCount})
          </Text>
          <RatingBadge>
            <IconLabel start="star" size={16} gap={4}>{averageRating.toFixed(1)} / 5.0</IconLabel>
          </RatingBadge>
        </ReviewHeader>

        {hasMyReview || reviewBlockedReason ? (
        <ReviewForm>
          <Text size="0.95rem" textcolor="#6b7280" margin="0" style={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
            {hasMyReview ? '이미 후기를 남겼어요.' : reviewBlockedReason}
          </Text>
        </ReviewForm>
        ) : (
        <ReviewForm>
          <Text size="1rem" fontweight="bold" margin="0 0 12px 0">
            리뷰 작성
          </Text>
          <ReviewTextarea
            value={reviewContent}
            onChange={(e) => setReviewContent(e.target.value)}
            placeholder={reviewPlaceholder}
            maxLength={2000}
            disabled={!canSubmitReview || isSubmittingReview}
          />
          <ReviewControls>
            <RatingSelect
              value={reviewRating}
              onChange={(e) => setReviewRating(Number(e.target.value))}
              disabled={!canSubmitReview || isSubmittingReview}
            >
              {[5, 4, 3, 2, 1].map(score => (
                <option key={score} value={score}>
                  {score}점
                </option>
              ))}
            </RatingSelect>
            <div style={{ flex: 1 }} />
            <Button
              type="button"
              variant="primary"
              onClick={handleReviewSubmit}
              disabled={!canSubmitReview || isSubmittingReview}
            >
              {isSubmittingReview ? <ButtonSpinner /> : '리뷰 등록'}
            </Button>
          </ReviewControls>
          {!user && (
            <Text size="0.9rem" textcolor="#94a3b8" margin="8px 0 0 0">
              로그인 후 리뷰를 작성할 수 있습니다.
            </Text>
          )}
          {user && !reviewEligibility && !isApprovedParticipant && (
            <Text size="0.9rem" textcolor="#94a3b8" margin="8px 0 0 0">
              승인된 참가자만 리뷰를 작성할 수 있습니다.
            </Text>
          )}
          {user && !reviewEligibility && isApprovedParticipant && !hasSeminarStarted && (
            <Text size="0.9rem" textcolor="#94a3b8" margin="8px 0 0 0">
              세미나 시작 이후에 리뷰를 작성할 수 있습니다.
            </Text>
          )}
        </ReviewForm>
        )}

        {reviews.length === 0 ? (
          <Text size="0.95rem" textcolor="#94a3b8">
            아직 등록된 리뷰가 없습니다. 첫 리뷰를 남겨보세요.
          </Text>
        ) : (
          <ReviewList>
            {reviews.map(review => (
              <ReviewCard key={review.id}>
                <ReviewMeta>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <strong style={{ color: '#111827' }}>{review.userNickname}</strong>
                    <span style={{ fontSize: 13, color: '#6b7280' }}>
                      {formatReviewDate(review.createdAt)}
                    </span>
                  </div>
                  {review.isAuthor && !review.isDeleted ? (
                    <div style={{ position: 'relative' }}>
                      <MoreButton onClick={() => toggleReviewMenu(review.id)}>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                        </svg>
                      </MoreButton>
                      <DropdownMenu isOpen={openReviewMenuId === review.id}>
                        <DropdownItem
                          onClick={() => startReviewEdit(review.id, review.content, review.rating)}
                        >
                          수정
                        </DropdownItem>
                        <DropdownItem
                          className="delete"
                          onClick={() => {
                            handleDeleteReview(review.id);
                            setOpenReviewMenuId(null);
                          }}
                        >
                          삭제
                        </DropdownItem>
                      </DropdownMenu>
                    </div>
                  ) : user && !review.isDeleted ? (
                    // 남의 후기 — 신고·차단(기존 게시글 신고 UI). 작성자는 서버가 후기 id 로 찾는다. 차단한 사용자의 후기는 서버가 빼고 내려준다
                    <ReportBlockMenu
                      targetType="SEMINAR_REVIEW"
                      targetId={review.id}
                      parentId={String(seminar.id)}
                      authorNickname={review.userNickname}
                      onBlocked={() => setSeminar((prev) => (prev
                        ? { ...prev, reviews: (prev.reviews ?? []).filter((r) => r.id !== review.id && r.userNickname !== review.userNickname) }
                        : prev))}
                    />
                  ) : (
                    <div style={{ width: 32, height: 32 }} />
                  )}
                </ReviewMeta>
                {review.isDeleted ? (
                  <DeletedReviewText>삭제된 리뷰 입니다.</DeletedReviewText>
                ) : editingReviewId === review.id ? (
                  <>
                    <ReviewTextarea
                      value={editingReviewContent}
                      onChange={(e) => setEditingReviewContent(e.target.value)}
                      maxLength={2000}
                    />
                    <ReviewControls>
                      <RatingSelect
                        value={editingReviewRating}
                        onChange={(e) => setEditingReviewRating(Number(e.target.value))}
                      >
                        {[5, 4, 3, 2, 1].map(score => (
                          <option key={score} value={score}>
                            {score}점
                          </option>
                        ))}
                      </RatingSelect>
                      <div style={{ flex: 1 }} />
                      {isMobile ? (
                        <>
                          <TextButton type="button" onClick={resetReviewEditing} disabled={isUpdatingReview}>
                            취소
                          </TextButton>
                          <TextButton type="button" onClick={handleReviewUpdate} disabled={isUpdatingReview}>
                            {isUpdatingReview ? <ButtonSpinner /> : '수정 완료'}
                          </TextButton>
                        </>
                      ) : (
                        <>
                          <Button type="button" onClick={resetReviewEditing} disabled={isUpdatingReview}>
                            취소
                          </Button>
                          <Button
                            type="button"
                            variant="primary"
                            onClick={handleReviewUpdate}
                            disabled={isUpdatingReview}
                          >
                            {isUpdatingReview ? <ButtonSpinner /> : '수정 완료'}
                          </Button>
                        </>
                      )}
                    </ReviewControls>
                  </>
                ) : (
                  <ReviewContent>{review.content}</ReviewContent>
                )}
                {editingReviewId !== review.id && (
                  <ReviewActions>
                    {/* 답글은 세미나 강사의 공식 답변이라 소유 강사에게만 노출 (비소유자는 서버가 409 Conflict 로 거절) */}
                    {seminar.isAuthor && (
                      <TextButton
                        type="button"
                        onClick={() => toggleReplyForm(review.id)}
                        disabled={review.isDeleted || isSubmittingReply}
                      >
                        댓글 달기
                      </TextButton>
                    )}
                    <RatingBadge><IconLabel start="star" size={14} gap={2}>{review.rating}</IconLabel></RatingBadge>
                  </ReviewActions>
                )}

                {review.replies && review.replies.length > 0 && (
                  <ReplyList>
                    {review.replies.map(reply => (
                      <ReplyCard key={reply.id}>
                        <ReplyMeta>
                          <span>{reply.userNickname}</span>
                          <ReplyMetaRight>
                            {reply.isAuthor ? (
                              <div style={{ position: 'relative' }}>
                                <MoreButton onClick={() => toggleReplyMenu(reply.id)}>
                                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                                  </svg>
                                </MoreButton>
                                <DropdownMenu isOpen={openReplyMenuId === reply.id}>
                                  <DropdownItem
                                    onClick={() => startReplyEdit(reply.id, reply.content)}
                                  >
                                    수정
                                  </DropdownItem>
                                  <DropdownItem
                                    className="delete"
                                    onClick={() => {
                                      handleReplyDelete(review.id, reply.id);
                                      setOpenReplyMenuId(null);
                                    }}
                                  >
                                    삭제
                                  </DropdownItem>
                                </DropdownMenu>
                              </div>
                            ) : (
                              <div style={{ width: 32, height: 32 }} />
                            )}
                          </ReplyMetaRight>
                        </ReplyMeta>
                        {editingReplyId === reply.id ? (
                          <>
                            <ReplyInput
                              value={editingReplyContent}
                              onChange={(e) => setEditingReplyContent(e.target.value)}
                              maxLength={1000}
                            />
                            <ReplyActions>
                              {isMobile ? (
                                <>
                                  <TextButton type="button" onClick={resetReplyEditing} disabled={isUpdatingReply}>
                                    취소
                                  </TextButton>
                                  <TextButton
                                    type="button"
                                    onClick={() => handleReplyUpdate(review.id)}
                                    disabled={isUpdatingReply}
                                  >
                                    {isUpdatingReply ? <ButtonSpinner /> : '수정 완료'}
                                  </TextButton>
                                </>
                              ) : (
                                <>
                                  <Button type="button" onClick={resetReplyEditing} disabled={isUpdatingReply}>
                                    취소
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="primary"
                                    onClick={() => handleReplyUpdate(review.id)}
                                    disabled={isUpdatingReply}
                                  >
                                    {isUpdatingReply ? <ButtonSpinner /> : '수정 완료'}
                                  </Button>
                                </>
                              )}
                            </ReplyActions>
                          </>
                        ) : (
                          <>
                            <ReplyContent>{reply.content}</ReplyContent>
                            <ReplyFooter>{formatReviewDate(reply.createdAt)}</ReplyFooter>
                          </>
                        )}
                      </ReplyCard>
                    ))}
                  </ReplyList>
                )}

                {activeReplyReviewId === review.id && !review.isDeleted && (
                  <div style={{ marginTop: 12 }}>
                    <ReplyInput
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      placeholder="댓글을 입력해 주세요. (최대 1,000자)"
                      maxLength={1000}
                      autoFocus
                      disabled={!seminar.isAuthor || isSubmittingReply}
                    />
                    <ReplyActions>
                      <Button
                        type="button"
                        onClick={() => {
                          setActiveReplyReviewId(null);
                          setReplyContent('');
                        }}
                        disabled={isSubmittingReply}
                      >
                        취소
                      </Button>
                      <Button
                        type="button"
                        variant="primary"
                        onClick={handleReplySubmit}
                        disabled={!seminar.isAuthor || isSubmittingReply}
                      >
                        댓글 등록
                      </Button>
                    </ReplyActions>
                  </div>
                )}
              </ReviewCard>
            ))}
          </ReviewList>
        )}
      </ReviewSection>
      )}

      {showActionSection && (
      <ActionSection>
        {seminar.isAuthor ? (
          // 작성자: 신청 버튼은 의미 없으므로 숨기고 수정만 메인 액션으로 노출.
          // 세미나가 시작되면 백엔드에서도 수정이 막히므로 버튼을 비활성화한다.
          <>
            <Button
              variant="primary"
              // 상시 동영상은 판매 시작 뒤에도 전체 수정할 수 있다(결제자가 있으면 수정 화면이 참가비·일정만 잠근다)
              disabled={hasSeminarStarted && !isOpenVod}
              onClick={() => navigate(`/seminars/${seminar.id}/edit`)}
            >
              세미나 수정
            </Button>
            {isOpenVod && (
              <Button
                variant="secondary"
                disabled={togglingSales}
                onClick={handleToggleSales}
                style={{ marginTop: 10 }}
              >
                {togglingSales ? <ButtonSpinner /> : salesClosed ? '판매 다시 시작' : '판매 종료'}
              </Button>
            )}
            {hasSeminarStarted && !isOpenVod && (
              <Text size="0.9rem" textcolor="#6b7280" margin="12px 0 0 0">
                이미 시작된 세미나는 수정할 수 없습니다.
              </Text>
            )}
            {isOpenVod && (
              <Text size="0.9rem" textcolor="#6b7280" margin="12px 0 0 0" style={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                {salesClosed
                  ? '지금은 판매를 멈춘 상태예요.\n이미 결제한 분은 계속 볼 수 있어요.'
                  : '판매를 멈춰도 이미 결제한 분은 계속 볼 수 있어요.'}
              </Text>
            )}
          </>
        ) : null /* 신청=하단 바/요약 카드, 취소=마이페이지 신청내역에서 처리 → 상세엔 미노출 */}
        {!seminar.isAuthor && recruitmentNotStarted && (
          <Text size="0.9rem" textcolor="#6b7280" margin="12px 0 0 0">
            모집 기간이 아직 시작되지 않았습니다.
          </Text>
        )}
      </ActionSection>
      )}

          {/* 일반(외부) 세미나 안내 — 화면 맨 아래 (PC 는 요약 카드 아래쪽에) */}
          {!isCareer && isMobileLayout && <GeneralNoticeCard />}

        </MainColumn>

        {!isMobileLayout && (
          <SideColumn>
            <DetailSummaryCard info={info} action={applyAction} onShare={handleShare} notes={summaryNotes} couponPrice={displayCoupon} paidPrice={paidPrice} />
          </SideColumn>
        )}
      </DetailPage>

      <CustomModal
        title='알림'
        width={isMobile ? '90%' : '400px'}
        onClose={closeAlert}
        open={showAlertModal}
        height='auto'
      >
        <ModalContainer width={"100%"} display='block'>
          <ModalText textalign='center' size='16px' margin='0 0 20px 0' style={{ whiteSpace: 'pre-line', lineHeight: 1.6, wordBreak: 'keep-all' }}>
            {alertMessage}
          </ModalText>
          <ModalButton
            onClick={closeAlert}
            justifycontents='center'
            color='#fff'
            bgcolor={designSystem.colors.primary}
          >
            확인
          </ModalButton>
        </ModalContainer>
      </CustomModal>

      <CustomModal
        title='확인'
        width={isMobile ? '90%' : '400px'}
        onClose={closeConfirm}
        open={showConfirmModal}
        height='auto'
      >
        <ModalContainer width={"100%"} display='block'>
          <ModalText textalign='center' size='16px' margin='0 0 20px 0' style={{ whiteSpace: 'pre-line', lineHeight: 1.65, textAlign: 'left' }}>
            {confirmMessage}
          </ModalText>
          <ModalContainer display='flex' gap='10px'>
            <ModalButton
              onClick={closeConfirm}
              justifycontents='center'
              color='#666'
              bgcolor='#e5e7eb'
            >
              취소
            </ModalButton>
            <ModalButton
              onClick={handleConfirm}
              justifycontents='center'
              color='#fff'
              bgcolor={designSystem.colors.primary}
            >
              확인
            </ModalButton>
          </ModalContainer>
        </ModalContainer>
      </CustomModal>

      {/* 유료 세미나 구매 확인 — 강의 정보 + 약관 */}
      <CustomModal
        title='세미나 신청'
        width={isMobile ? '92%' : '440px'}
        onClose={() => setShowPurchaseModal(false)}
        open={showPurchaseModal}
        height='auto'
      >
        <ModalContainer width={"100%"} display='block'>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 16 }}>
            {(seminar.thumbnailUrl || seminar.thumbnail) && (
              <img
                src={seminar.thumbnailUrl || seminar.thumbnail}
                alt={seminar.title}
                style={{ width: 96, height: 72, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }}
              />
            )}
            <div style={{ minWidth: 0, flex: 1 }}>
              <ModalText size='15px' fontweight='700' style={{ wordBreak: 'keep-all', lineHeight: 1.4, marginBottom: 4 }}>
                {seminar.title}
              </ModalText>
              <ModalText size='13px' textcolor='#6b7280' style={{ marginBottom: 2 }}>
                강사: {seminar.instructorNickname || seminar.instructor}
              </ModalText>
              <ModalText size='13px' textcolor='#6b7280'>
                일정: {info.scheduleText}
              </ModalText>
            </div>
          </div>

          {id && (
            <PurchaseCouponPicker seminarId={id} open={showPurchaseModal} onChange={setPurchaseCoupon} onLoadingChange={setPurchaseCouponsLoading} />
          )}

          {/* 참가비 / 쿠폰 할인 / 결제 금액 — 결제 금액 = 서버가 계산한 할인액 기준(prepare 에서 다시 확인) */}
          <div style={{ padding: '12px 0', borderTop: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0', marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {purchaseCoupon && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <ModalText size='14px' textcolor='#374151'>참가비</ModalText>
                  <ModalText size='14px' textcolor='#374151' style={{ fontVariantNumeric: 'tabular-nums' }}>{won(listPrice)}</ModalText>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <ModalText size='14px' textcolor='#374151'>쿠폰 할인</ModalText>
                  <ModalText size='14px' textcolor='#dc2626' style={{ fontVariantNumeric: 'tabular-nums' }}>−{won(purchaseCoupon.discountAmount)}</ModalText>
                </div>
              </>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <ModalText size='14px' textcolor='#374151'>결제 금액</ModalText>
              <ModalText size='18px' fontweight='700' textcolor={designSystem.colors.primary} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {won(purchaseAmount)}
              </ModalText>
            </div>
          </div>

          {/* 지금 결제하면 환불 일정 — 기본 접힘(결제 버튼이 밀리지 않게). 접혀도 첫 구간 요약과 중요한 한 줄(동영상 접속 후 환불 불가 등)은 보인다 */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <ModalText size='13px' fontweight='700' textcolor='#374151'>
                지금 결제하면 환불 일정
              </ModalText>
              <button
                type='button'
                aria-expanded={refundOpen}
                aria-controls='purchase-refund-detail'
                onClick={() => setRefundOpen((prev) => !prev)}
                style={{ padding: '4px 0 4px 8px', border: 0, background: 'none', color: '#6b7280', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                {refundOpen ? '접기 ⌃' : '자세히 ⌄'}
              </button>
            </div>
            {!refundOpen && refundSummary && (
              <div style={{ marginTop: 4, fontSize: 13, color: '#374151' }}>{refundSummary}</div>
            )}
            {/* 중요한 한 줄 — 접혀 있어도 요약 아래에 */}
            {purchaseNoteLines.length > 0 && (
              <div style={{ marginTop: 4, fontSize: 12, lineHeight: 1.6 }}>
                {purchaseNoteLines.map((line: string) => (
                  <div key={line} style={{ color: '#b45309', fontWeight: 700 }}>{line}</div>
                ))}
              </div>
            )}
            {refundOpen && (
              <div id='purchase-refund-detail' style={{ marginTop: 8 }}>
                {scheduleWindowLines.length > 0 ? (
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                    {scheduleWindowLines.map((line, index) => {
                      const rate: number = purchaseSchedule?.windows[index]?.refundRate ?? 0;
                      const [when, what] = line.split(': ');
                      const tone = rate === 100
                        ? { bg: '#ecfdf5', fg: '#047857' }
                        : rate > 0 ? { bg: '#fffbeb', fg: '#b45309' } : { bg: '#fef2f2', fg: '#b91c1c' };
                      return (
                        <div
                          key={line}
                          style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                            padding: '10px 12px', borderTop: index === 0 ? 'none' : '1px solid #f0f0f0',
                          }}
                        >
                          <span style={{ fontSize: 13, color: '#374151', wordBreak: 'keep-all' }}>{when}</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: tone.fg, background: tone.bg, borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap' }}>
                            {what}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : scheduleNoteLines.length === 0 && !isOpenVod ? (
                  <div style={{ fontSize: 12, lineHeight: 1.7, color: '#6b7280' }}>
                    {REFUND_POLICY_RULES.map((r) => (
                      <div key={r.rate}>· {r.text}</div>
                    ))}
                  </div>
                ) : null}
              </div>
            )}
            {/* 동의 문구가 가리키는 대상 — 폐강·거절 전액 환불, 제공 기간, 통신판매중개자 고지, 정책 전문 링크는 늘 보인다 */}
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8, lineHeight: 1.6 }}>
              <div>강사 사정으로 폐강되거나 신청이 거절되면 기간과 상관없이 전액 환불됩니다.</div>
            </div>
          <div style={{ fontSize: 11, lineHeight: 1.7, color: '#9ca3af', marginTop: 8 }}>
            {isOpenVod ? (
              <div>· 서비스 제공 기간: 결제 완료 시점부터 계속이에요(판매가 끝나도 볼 수 있어요).</div>
            ) : (
              <>
                <div>· 결제 후 7일 이내에는 세미나 시작 전이라면 전액 환불됩니다.</div>
                <div>· 서비스 제공 기간: 결제 완료 시점부터 위 세미나 일시(종료)까지입니다.</div>
              </>
            )}
            <div>· 명치사는 통신판매중개자로 거래 당사자가 아니며, 세미나 내용·진행 책임은 강사에게 있습니다.</div>
            <div style={{ marginTop: 6 }}>
              <a href="/refund-policy" target="_blank" rel="noopener noreferrer" style={{ color: '#6b7280', textDecoration: 'underline' }}>
                결제·환불 정책 전문 보기
              </a>
            </div>
          </div>
          </div>

          <label
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 8, padding: '12px', marginBottom: 12,
              background: '#f8faf9', borderRadius: 10, cursor: 'pointer',
            }}
          >
            <input
              type='checkbox'
              checked={purchaseAgreed}
              onChange={(e) => setPurchaseAgreed(e.target.checked)}
              style={{ width: 18, height: 18, marginTop: 1, accentColor: designSystem.colors.primary, flexShrink: 0 }}
            />
            <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.5, wordBreak: 'keep-all' }}>
              위 환불 일정과 결제·환불 정책을 확인했습니다.
            </span>
          </label>

          {/* 쿠폰 안내 — 모달 맨 아래, 문장마다 줄바꿈 */}
          {purchaseCoupon && (
            <div style={{ fontSize: 11, lineHeight: 1.7, color: '#9ca3af', marginTop: 12 }}>
              <div>
                · {purchaseCoupon.restoreOnRefund ? COUPON_RESTORE_NOTICE : COUPON_NO_RESTORE_NOTICE}
              </div>
              <div>· 환불은 실제 결제한 {won(purchaseAmount)} 기준이에요.</div>
              <div>· 쿠폰은 할인권이라 현금으로 바꾸거나 다른 사람에게 줄 수 없어요.</div>
            </div>
          )}

          {/* 결제 버튼은 모달 아래에 늘 보이게(sticky — 모달 본문만 스크롤, fixed 금지) */}
          <div style={{ position: 'sticky', bottom: 0, zIndex: 1, margin: '12px -20px -20px', padding: '12px 20px 20px', background: '#fff', borderTop: '1px solid #f0f0f0' }}>
            <div style={{ display: 'flex', gap: 10 }}>
              <ModalButton
                onClick={() => setShowPurchaseModal(false)}
                justifycontents='center'
                color='#666'
                bgcolor='#e5e7eb'
                style={{ flex: 1 }}
              >
                취소
              </ModalButton>
              <ModalButton
                onClick={() => {
                  if (!purchaseAgreed || purchaseCouponsLoading) return;
                  setShowPurchaseModal(false);
                  void startPaidApplication(purchaseCoupon);
                }}
                disabled={!purchaseAgreed || purchaseCouponsLoading}
                justifycontents='center'
                color='#fff'
                bgcolor={purchaseAgreed && !purchaseCouponsLoading ? designSystem.colors.primary : '#cbd5d4'}
                style={{ flex: 1, cursor: purchaseAgreed && !purchaseCouponsLoading ? 'pointer' : 'not-allowed' }}
              >
                {won(purchaseAmount)} 결제
              </ModalButton>
            </div>
          </div>
        </ModalContainer>
      </CustomModal>

      {/* 유료 세미나 취소·환불 — 강의 정보 카드 + 환불 내역 */}
      <CustomModal
        title='세미나 신청 취소'
        width={isMobile ? '92%' : '440px'}
        onClose={() => setShowCancelModal(false)}
        open={showCancelModal}
        height='auto'
      >
        <ModalContainer width={"100%"} display='block'>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 16 }}>
            {(seminar.thumbnailUrl || seminar.thumbnail) && (
              <img
                src={seminar.thumbnailUrl || seminar.thumbnail}
                alt={seminar.title}
                style={{ width: 96, height: 72, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }}
              />
            )}
            <div style={{ minWidth: 0, flex: 1 }}>
              <ModalText size='15px' fontweight='700' style={{ wordBreak: 'keep-all', lineHeight: 1.4, marginBottom: 4 }}>
                {seminar.title}
              </ModalText>
              <ModalText size='13px' textcolor='#6b7280' style={{ marginBottom: 2 }}>
                강사: {seminar.instructorNickname || seminar.instructor}
              </ModalText>
              <ModalText size='13px' textcolor='#6b7280'>
                일정: {info.scheduleText}
              </ModalText>
            </div>
          </div>

          {refundQuote?.cancellable ? (
            <div style={{ padding: '12px 0', borderTop: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0', marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <ModalText size='13px' textcolor='#9ca3af'>결제 금액</ModalText>
                <ModalText size='13px' textcolor='#374151'>{(refundQuote.originalAmount ?? 0).toLocaleString()}원</ModalText>
              </div>
              {refundQuote.coupon && refundQuote.coupon.discountAmount > 0 && (
                <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'right', marginTop: -4 }}>
                  정가 {won(refundQuote.coupon.listAmount)} − 쿠폰 {won(refundQuote.coupon.discountAmount)}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <ModalText size='14px' textcolor='#374151'>실제 환불 금액</ModalText>
                <ModalText size='18px' fontweight='700' textcolor={designSystem.colors.primary}>
                  {(refundQuote.refundAmount ?? 0).toLocaleString()}원 ({refundQuote.refundRate ?? 0}%)
                </ModalText>
              </div>
              {refundQuote.coupon && (
                <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
                  {refundQuote.coupon.notice || cancelCouponLine(refundQuote.coupon.couponName, refundQuote.coupon.restored)}
                </div>
              )}
            </div>
          ) : (
            <div style={{ padding: '12px 0', borderTop: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0', marginBottom: 12 }}>
              <ModalText size='14px' fontweight='700' textcolor='#dc2626' textalign='center'>
                환불 불가 — 취소해도 환불되지 않습니다
              </ModalText>
            </div>
          )}

          <div style={{ fontSize: 11, lineHeight: 1.7, color: '#9ca3af', marginBottom: 16 }}>
            <div style={{ fontWeight: 700, color: '#6b7280', marginBottom: 4 }}>환불 규정</div>
            {refundQuote?.reason && (
              <div style={{ color: designSystem.colors.primary, fontWeight: 700 }}>· 현재 적용: {refundQuote.reason}</div>
            )}
            {/* 상시 동영상은 '시작 전' 규칙 대신 결제 후 7일·접속 전 규칙(아래 줄) */}
            {!isOpenVod && <div>· {REFUND_WITHDRAWAL_RULE}</div>}
            {!isOpenVod && REFUND_POLICY_RULES.map((r) => (
              <div key={r.rate}>· {r.text}</div>
            ))}
            <div>· 강사가 참가 신청을 거절하거나 강사 귀책으로 폐강 시 전액(100%) 환불됩니다.</div>
            {vodNotes.map((line: string) => (
              <div key={line}>· {line}</div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <ModalButton
              onClick={() => setShowCancelModal(false)}
              justifycontents='center'
              color='#666'
              bgcolor='#e5e7eb'
              style={{ flex: 1 }}
            >
              닫기
            </ModalButton>
            <ModalButton
              onClick={() => { setShowCancelModal(false); doCancel(); }}
              justifycontents='center'
              color='#fff'
              bgcolor={refundQuote?.cancellable ? designSystem.colors.primary : '#dc2626'}
              style={{ flex: 1 }}
            >
              {refundQuote?.cancellable ? '취소하고 환불받기' : '환불 없이 취소'}
            </ModalButton>
          </div>
        </ModalContainer>
      </CustomModal>

      {/* 모바일 하단 바 — sticky 라 페이지 컨테이너의 마지막 자식이어야 페이지 내내 붙어 있다 */}
      {isMobileLayout && applyAction && (
        <ApplyBottomBar
          priceText={paidPrice ? won(paidPrice.amount) : displayCoupon ? won(displayCoupon.price) : info.feeText}
          couponRate={!paidPrice && displayCoupon ? displayCoupon.rate : null}
          caption={paidPrice ? '결제 금액' : undefined}
          action={applyAction}
        />
      )}
    </CustomPageContainer>
  );
};

export default SeminarDetail;